import { useMemo } from "react";
import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  WORKSPACE_GROUPS_TABLE_COLUMN_LABELS,
  WorkspaceGroupsTableColumnField,
  WorkspaceGroupsTableGridColDef,
} from "@pages/workspace-profile/tab-contents/workspace-groups-tab/workspace-groups-table-types";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  GroupCreatedAtCell,
  GroupManagerCountCell,
  GroupNameCell,
  GroupOptionsCell,
  GroupProjectCountCell,
  GroupSqftAssignedCell,
  GroupSqftUsedCell,
} from "@utils/data-grid-utils/groups-data-grid-cells";

const columnDimension: ColumnDimension<WorkspaceGroupsTableColumnField> = {
  name: {
    flex: 1.5,
  },
  managerCount: {
    flex: 0.75,
  },
  projectCount: {
    flex: 1,
  },
  sqftUsed: {
    flex: 1,
  },
  sqftAssigned: {
    flex: 1.5,
  },
  createdDate: {
    flex: 1.5,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the workspace's groups table
 */
export function useWorkspaceGroupsTableColumns({
  companyId,
}: BaseCompanyIdProps): WorkspaceGroupsTableGridColDef[] {
  const columnsMap: Record<
    WorkspaceGroupsTableColumnField,
    WorkspaceGroupsTableGridColDef
  > = useMemo(() => {
    return {
      name: {
        field: "name",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.name,
        ...columnDimension.name,
        valueGetter: (_, row) => row.entity.name,
        renderCell: GroupNameCell,
      },

      managerCount: {
        field: "managerCount",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.managerCount,
        ...columnDimension.managerCount,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.managerCount,
        renderCell: GroupManagerCountCell,
      },

      projectCount: {
        field: "projectCount",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.projectCount,
        ...columnDimension.projectCount,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.projectCount,
        renderCell: GroupProjectCountCell,
      },

      sqftUsed: {
        field: "sqftUsed",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.sqftUsed,
        ...columnDimension.sqftUsed,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.sqft.used,
        renderCell: GroupSqftUsedCell,
      },

      sqftAssigned: {
        field: "sqftAssigned",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.sqftAssigned,
        ...columnDimension.sqftAssigned,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.sqft.assigned,
        renderCell: GroupSqftAssignedCell,
      },

      createdDate: {
        field: "createdDate",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.createdDate,
        ...columnDimension.createdDate,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.createdAt,
        renderCell: GroupCreatedAtCell,
      },

      options: {
        field: "options",
        type: "actions",
        headerName: WORKSPACE_GROUPS_TABLE_COLUMN_LABELS.options,
        ...columnDimension.options,
        align: "right",
        renderCell: (params) => GroupOptionsCell(params, companyId),
      },
    };
  }, [companyId]);

  return Object.values(columnsMap);
}
